import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './apiClient';

export interface ModuleDependency {
  key: string;
  name: string;
  type: 'Required' | 'Optional';
  enabled: boolean;
}

/** A platform module as the organisation stands with it: subscribed, switched on, set up, and by whom. */
export interface ModuleState {
  key: string;
  name: string;
  description: string;
  version: string;
  isCore: boolean;
  allowed: boolean;
  enabled: boolean;
  configured: boolean;
  enabledVersion: string | null;
  allowedAt: string | null;
  allowedBy: string | null;
  enabledAt: string | null;
  enabledBy: string | null;
  disabledAt: string | null;
  disabledBy: string | null;
  dependencies: ModuleDependency[];
  requiredBy: string[];
  permissions: string[];
  configureRoute: string | null;
}

/** The modules the signed-in user's organisation has; drives navigation and page gating. Refetched on focus so a change made elsewhere is picked up. */
export function useMyModules(enabled = true) {
  return useQuery({
    queryKey: ['modules', 'me'],
    queryFn: async () => (await apiClient.get<ModuleState[]>('/api/v1/modules/me')).data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled,
  });
}

/** Every deployed module with its full state, for the administration page (Settings.Manage). */
export function useModules() {
  return useQuery({
    queryKey: ['modules', 'all'],
    queryFn: async () => (await apiClient.get<ModuleState[]>('/api/v1/modules')).data,
  });
}

function useSwitchModule(action: 'enable' | 'disable') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => (await apiClient.put<ModuleState>(`/api/v1/modules/${key}/${action}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['modules'] }),
  });
}

/** Switches a subscribed module on for the organisation. */
export const useEnableModule = () => useSwitchModule('enable');
/** Switches a module off; its data is kept. */
export const useDisableModule = () => useSwitchModule('disable');

// ---------------------------------------------------------------------------------------------
// The platform operator's side: another organisation's modules, from the host organisation.
// ---------------------------------------------------------------------------------------------

export type OrganisationModuleAction = 'allow' | 'revoke' | 'enable' | 'disable';

export function useOrganisationModules(id: string | null) {
  return useQuery({
    queryKey: ['tenants', id, 'modules'],
    queryFn: async () => (await apiClient.get<ModuleState[]>(`/api/v1/tenants/${id}/modules`)).data,
    enabled: id !== null,
  });
}

/** Subscribes (allow), withdraws (revoke), enables or disables a module for an organisation. */
export function useSetOrganisationModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, key, action }: { id: string; key: string; action: OrganisationModuleAction }) =>
      (await apiClient.put<ModuleState>(`/api/v1/tenants/${id}/modules/${key}/${action}`)).data,
    onSuccess: (_state, { id }) => qc.invalidateQueries({ queryKey: ['tenants', id, 'modules'] }),
  });
}
